(ns app.front.view.public-records.common
  (:require [app.domain.group.config.model :as config-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.municipality.model :as municipality-model]
            [app.domain.property.model :as property-model]
            [app.domain.public-map.model :as public-map-model]
            [app.domain.util :as du]
            [app.front.ui :as ui]
            [app.front.ui.multi-select :as ui-ms]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.ui.section :as section]
            [app.front.util :as futil]
            [app.front.view.create-application.gis-search :as gis-search]
            [app.front.view.landing-page-subs :as landing-page-subs]
            [app.front.view.map-common.info-box :as map-common-info-box]
            [app.front.view.map-common.util :as map-common-util]
            [app.i18n.model :as i18n-model]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.front.event :refer [<sub >evt]]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

;; ---- Data path interceptor --------------------------------------------------
;; All events in this feature share this single state map via data-path.

(def data-path (rf/path ::data))

;; ---- Constants --------------------------------------------------------------

(def results-layer-id "results-layer")
(def complaint-results-layer-id "complaint-results-layer")
(def combined-results-layer-id "combined-results-layer")

(def tab-order
  {:public-records/development              0
   :public-records/periodic-inspections     1
   :public-records/cases                    2
   :public-records/work-orders              3
   :public-records/licences                 4
   :public-records/contractor-registrations 5
   :public-records/complaints               6})

(def application-tabs
  #{:public-records/development
    :public-records/periodic-inspections
    :public-records/cases
    :public-records/work-orders})

;; Elasticsearch's default max_result_window — the effective ceiling for map
;; searches, which do not paginate. ES rejects from + size > max_result_window,
;; so this limit is only safe while those searches send no :skip.
(def unpaginated-search-limit 10000)

;; ---- Pure helpers -----------------------------------------------------------

(defn show-filter?
  "Returns true when the option collection has at least two values.
  Used to hide any Public Records filter with 0 or 1 available options."
  [options]
  (du/at-least-two? options))

(defn visible-filter-attributes
  "Return filter attribute keys whose visibility options currently have enough
   values to justify showing the filter, or which currently have a selection."
  [filter-configs selected-options]
  (->> filter-configs
       (filter (fn [{:keys [attribute visibility-options]}]
                 (or (show-filter? visibility-options)
                     (seq (get selected-options attribute)))))
       (map :attribute)
       set))

(defn initial-visible-filters
  "Return the filters that should be visible for the current response based on
   the latest available filter options and current valid selections."
  [_current-visible-filters filter-configs selected-options]
  (visible-filter-attributes filter-configs selected-options))

(defn render-search-filters
  [tr visible-filters filter-configs]
  (when (seq visible-filters)
    [:div.bg-ui-light-gray.flex.flex-col.rounded-lg.p-4
     [:h3.mb-0 (tr [:search/search])]
     (into [:div.flex.flex-wrap.-mx-2]
           (keep (fn [{:keys [attribute id options value on-change label-tr-key val->str]}]
                   (when (contains? visible-filters attribute)
                     (let [label (tr [label-tr-key])]
                       ^{:key id}
                       [:div.w-full.md:w-1-2.lg:w-1-3.xl:w-1-4.px-2.mt-2
                        [ui-ms/filterable-multi-select
                         {:id          id
                          :values      options
                          :val->str    val->str
                          :on-change   on-change
                          :value       value
                          :label-text  label
                          :placeholder label
                          :hide-label? true
                          :light       true}]])))
                 filter-configs))]))

(defn category-name [workspace lang]
  (-> workspace
      :category-names
      (i18n-model/get-text-from-entities-or-map lang)))

(defn selected-options->search-options [selected-options]
  (p/assoc-if {}
              :municipalities (some->> selected-options
                                       :municipalities
                                       (keep :municipality/number)
                                       set
                                       not-empty)
              :permit-types (some->> selected-options
                                     :permit-types
                                     not-empty)
              :categories (some->> selected-options
                                   :categories
                                   (keep :db/id)
                                   set
                                   not-empty)))

(defn available-category-options [available-filters permit-type-configs]
  (when-some [category-ids (not-empty (:categories available-filters))]
    (->> (vals permit-type-configs)
         (mapcat :categories)
         (filter #(contains? category-ids (:db/id %)))
         set
         not-empty)))

(defn sorted-category-options [available-filters permit-type-configs lang]
  (some->> (available-category-options available-filters permit-type-configs)
           (config-model/sort-group-configs lang)))

(defn search-filter-configs
  "Builds the filter config vector for render-search-filters.
   Pure function — all reactive values must be resolved by the caller.
   `id-prefix`        – unique string prefix for DOM ids (e.g. \"\" or \"complaint-\")
   `selected-options`  – current selected option sets per attribute
   `set-options-fn`    – (fn [attribute values]) dispatches the selection change
   `lang`              – language keyword
   `permit-type-names` – map of permit-type keyword to display name
   `municipality-options`, `permit-type-options`, `category-options` – available filter values"
  [{:keys [id-prefix selected-options set-options-fn
           lang permit-type-names
           municipality-options permit-type-options category-options]}]
  [{:attribute    :permit-types
    :id           (str id-prefix "permit-types-filter")
    :options      (or permit-type-options [])
    :value        (:permit-types selected-options)
    :on-change    #(set-options-fn :permit-types %)
    :label-tr-key :public-records/permit-type
    :val->str     #(get permit-type-names %)}
   {:attribute    :categories
    :id           (str id-prefix "categories-filter")
    :options      (or category-options [])
    :value        (:categories selected-options)
    :on-change    #(set-options-fn :categories %)
    :label-tr-key :public-records/category
    :val->str     #(config-model/localized-name % lang)}
   {:attribute    :municipalities
    :id           (str id-prefix "municipalities-filter")
    :options      (or municipality-options [])
    :value        (:municipalities selected-options)
    :on-change    #(set-options-fn :municipalities %)
    :label-tr-key :public-records/municipality
    :val->str     #(municipality-model/municipality-name % lang)}])

(def ^:private prune-filter-configs
  "Descriptor for each filter used by `prune-selected-options`.
   :key           – the attribute key in both selected-options and available-filters
   :id-fn         – extracts the stable identifier for an option"
  [{:key :municipalities :id-fn :municipality/number}
   {:key :permit-types :id-fn identity}
   {:key :categories :id-fn :db/id}])

(defn- available-option-ids [options id-fn]
  (some->> options
           (keep (fn [option]
                   (if (map? option)
                     (id-fn option)
                     option)))
           set))

(defn prune-selected-options
  "Drop any selected Public Records filters that backend available-filters no longer allow."
  [selected-options available-filters]
  (reduce (fn [acc {:keys [key id-fn]}]
            (let [available-ids (available-option-ids (get available-filters key #{}) id-fn)
                  pruned        (some->> (get selected-options key)
                                         (filter #(contains? available-ids (id-fn %)))
                                         set
                                         not-empty)]
              (cond-> acc
                pruned (assoc key pruned))))
          {}
          prune-filter-configs))

(defn visible-complaint-permit-type-config-map [permit-type-configs]
  (->> permit-type-configs
       (filter (fn [[permit-type config]]
                 (and (ptc-model/permit-types-with-complaints permit-type)
                      (not (:group.config/public-map-hide-complaints config)))))
       (into {})))

(defn result-permit-type
  "Returns the permit type from a search result regardless of entity type.
   Applications use :workspace/permit-type, complaints use :permit-type."
  [result]
  (or (:workspace/permit-type result) (:permit-type result)))

(defn gis-data->selected-property
  "Converts an `:address/point-search` response (GisData, nested shape) into the
   shape expected by `selected-property` consumers. Unlike ES search results, the
   GisData geometry is already in map projection, so it is used as-is."
  [gis-data]
  (p/assoc-if {:property (select-keys (:property gis-data) [:property/id])}
              :address      (when-some [addr (-> gis-data :address :address/full-address)]
                              {:address/full-address addr})
              :geometry     (:geometry gis-data)
              :municipality (:municipality gis-data)))

(defn search-results->feature-collection [search-results selected-result selected-cluster-ids]
  (->> search-results
       (keep (fn [{:keys [location id] :as result}]
               (when location
                 {:properties (-> result
                                  (dissoc :location :id)
                                  (assoc :selected (if-some [selected-id (:id selected-result)]
                                                     (= id selected-id)
                                                     (contains? selected-cluster-ids id))))
                  :type       "Feature"
                  :geometry   location
                  :id         id})))
       property-model/geojson-feature-collection))

(defn feature-collection->map-layer
  ([tr feature-collection]
   (feature-collection->map-layer results-layer-id (tr [:public-records/public-records]) feature-collection))
  ([layer-id title feature-collection]
   [{:type                    "geojson-cluster"
     :id                      layer-id
     :title                   title
     :clusterMarkerSelectable true
     :featureCollection       feature-collection}]))

(defn format-attribute-value [{:keys [tr lang attribute entity format-date-time]}]
  (when-some [value (attribute entity)]
    (cond
      (keyword? value)
      (tr [value])

      (inst? value)
      (format-date-time value)

      (boolean? value)
      (tr [(if value :yes :no)])

      (= attribute :workspace/case-description)
      (let [id (str "case-description-text-editor" (:id entity))]
        ^{:key id}
        [ui-rt/editor-legacy {:initial-value value
                              :id            id}])

      (#{:category-names :application/work-type-names :application/work-target-names}
       attribute)
      (i18n-model/get-text-from-entities-or-map value lang)

      (vector? value)
      (str/join ", " value)

      :else
      value)))

(defn record-type-label [tr entity-type]
  (tr [(case entity-type
         :entity.type/complaint :public-records/complaint
         :entity.type/workspace :public-records/case
         :public-records/case)]))

(defn format-detail-attrs
  "Formats a list of `[attribute label-key]` pairs into `[translated-label formatted-value]`
   pairs, keeping only attributes with non-nil values."
  [{:keys [tr lang entity format-date-time]} attribute-label-pairs]
  (keep (fn [[attribute label-key]]
          (when-some [value (format-attribute-value {:tr               tr
                                                     :lang             lang
                                                     :attribute        attribute
                                                     :entity           entity
                                                     :format-date-time format-date-time})]
            [(tr [label-key]) value]))
        attribute-label-pairs))

(defn configured-public-map-attributes [workspace permit-type-configs]
  (-> workspace
      result-permit-type
      permit-type-configs
      :group.config/visible-workspace-attrs-in-public-map))

(defn available-tab-permit-types [permit-type-configs tab]
  (when-some [tab-permit-types (public-map-model/tab->permit-types tab)]
    (set/intersection tab-permit-types
                      (-> permit-type-configs keys set))))

(defn tab-idx->tab [tabs tab-idx]
  (nth tabs tab-idx nil))

(defn on-sort-hook [event-id attribute previous-sort-order]
  ;; The table passes the sort state *before* toggling;
  ;; we invert it here to match the new server-side sort direction.
  (let [sort-order (if (= previous-sort-order :asc) :desc :asc)]
    (>evt [event-id {:sort-condition [attribute sort-order]
                     :keep-results?  false}])))

(defn default-sort-for-tab [tab]
  (case tab
    (:public-records/cases :public-records/work-orders)
    [:created :desc]

    :public-records/licences
    [:licence/valid-from :desc]

    :public-records/contractor-registrations
    [:contractor-registration/valid-from :desc]

    :public-records/periodic-inspections
    [:workspace/municipal-case-id :desc]

    ;; development and default
    [:submitted :desc]))

(defn sort-condition->initial-sorting [sort-condition columns]
  (when-let [[sort-attribute sort-order] sort-condition]
    (let [column-extractor (some (fn [[_ extractor {:keys [sort-attribute-key]}]]
                                   (when (= sort-attribute-key sort-attribute)
                                     extractor))
                                 columns)]
      [(or column-extractor sort-attribute) sort-order])))

(defn location-coordinates [{:keys [coordinates]}]
  (some->> (seq coordinates)
           reverse ; show latitude first
           (map #(futil/to-fixed % 5))
           (str/join ", ")))

(defn search-scope
  "How a search is narrowed down, as a map of the command keys it implies.

   A selected property owns the slot when there is one, the current viewport
   extent otherwise — pass `extent-polygon` only in map view, the list has no
   viewport. Returns nil when neither applies.

   A property is normally scoped by its polygon: intersecting the parcel's
   *current* borders against indexed locations is what answers \"what has ever
   been within these borders\", and is what makes the search immune to parcels
   being split, merged and renumbered over time.

   Not every GIS has parcel polygons though — CGIS returns a point and nothing
   else (`app.address.cgis.cgis-generic`), which is why `:geometry` is an optional
   key on the address and point search responses. For those the scope falls back
   to the property id, narrowed to the property's own municipality because an id
   is only unique within one. Without the fallback a polygon-less property drops
   through to the viewport extent, and the map and the property panel then present
   whatever happens to be on screen as \"what is on this parcel\".

   Never both: the id answers \"what claims this parcel number\", the polygon
   answers \"what lies within these borders\". They are different questions and
   combining them would blur the two."
  [selected-property extent-polygon]
  (if selected-property
    (if-some [geometry (:geometry selected-property)]
      {:shape geometry}
      ;; Neither a polygon nor an id means the property cannot scope a search at
      ;; all. Returning nil then leaves the search merely unscoped — the extent
      ;; branch below is unreachable here, so the viewport can never end up
      ;; standing in for the parcel.
      (when-some [property-id (-> selected-property :property :property/id)]
        (p/assoc-if {:property-id property-id}
                    :municipality (:municipality selected-property))))
    (when extent-polygon
      {:shape {:type "Polygon" :coordinates extent-polygon}})))

(defn- narrow-municipalities [selected municipality]
  (if (seq selected)
    (set/intersection selected #{municipality})
    #{municipality}))

(defn assoc-search-scope
  "Applies a `search-scope` to search command data. An id scope also narrows
   :municipalities to the property's own, intersected with whatever the user
   selected — an empty intersection means the property is outside the user's
   filter, and no results is the right answer."
  [command-data {:keys [shape property-id municipality]}]
  (cond-> command-data
    shape                         (assoc :shape shape)
    property-id                   (assoc :property-id property-id)
    (and property-id municipality) (update :municipalities narrow-municipalities municipality)))

(defn search-skip [current-skip keep-results?]
  (if keep-results? current-skip 0))

(defn next-skip [current-skip limit]
  (+ current-skip limit))

(defn merge-search-results [existing-results new-results keep-results?]
  (if keep-results?
    (into (vec existing-results) new-results)
    new-results))

(defn render-load-more-button
  [tr in-progress? remaining-count {:keys [load-more-evt]}]
  (when (pos? remaining-count)
    [:div.flex.justify-center.mt-4
     [ui/button {:size     :small
                 :kind     :tertiary
                 :on-click #(>evt [load-more-evt])
                 :disabled in-progress?}
      (tr [:public-records/load-more] [remaining-count])]]))

(defn render-paginated-table
  [{:keys [tr in-progress? loading-more? results total-count table]
    :as   load-more-options}]
  (if (and in-progress? (not loading-more?))
    [section/section-skeleton]
    [:div
     table
     [render-load-more-button tr in-progress? (- total-count (count results)) load-more-options]]))

(defn make-load-more-handler [{:keys [skip-key limit-key search-evt]
                               :or   {skip-key  :skip
                                      limit-key :limit}}]
  (fn [{:keys [db]} _]
    (let [new-skip (next-skip (get db skip-key) (get db limit-key))]
      {:db (assoc db skip-key new-skip)
       :fx [[:dispatch [search-evt {:keep-results? true}]]]})))

;; ---- Shared search.success handler factory ----------------------------------

(defn make-search-success-handler
  "Returns a search.success handler parameterised by the db keys used by the tab.
   Movement-driven responses retain the broader filter facets from the last user
   search, and selection pruning refreshes both map layers."
  [{:keys [total-count-key available-filters-key selected-options-key
           search-results-key visible-filters-key visible-filter-configs-fn
           search-evt secondary-refresh-fx-fn from-map-move-flag-key request-generation-key]}]
  (fn [{:keys [db]}
       [_ {:keys [keep-results? from-map-move? request-generation]}
        _status
        {:keys [total-count results available-filters]}]]
    (when (or (nil? request-generation)
              (= request-generation (get db request-generation-key)))
      (let [available-filters      (if (and from-map-move? (get db available-filters-key))
                                     (get db available-filters-key)
                                     available-filters)
            selected-options       (if from-map-move?
                                     (get db selected-options-key)
                                     (prune-selected-options (get db selected-options-key)
                                                             available-filters))
            visible-filter-configs (when visible-filter-configs-fn
                                     (visible-filter-configs-fn db available-filters))
            visible-filters        (when visible-filters-key
                                     (initial-visible-filters (get db visible-filters-key)
                                                              visible-filter-configs
                                                              selected-options))
            selections-changed?    (not= selected-options (get db selected-options-key))
            updated-db             (cond-> (assoc db
                                                  total-count-key total-count
                                                  available-filters-key available-filters
                                                  selected-options-key selected-options
                                                  search-results-key (merge-search-results
                                                                       (get db search-results-key)
                                                                       results
                                                                       keep-results?))
                                     visible-filters-key
                                     (assoc visible-filters-key visible-filters)

                                     from-map-move-flag-key
                                     (assoc from-map-move-flag-key from-map-move?))]
        {:db updated-db
         :fx (cond-> []
               selections-changed?
               (conj [:dispatch [search-evt {:keep-results? false}]])

               (and selections-changed? secondary-refresh-fx-fn)
               (into (secondary-refresh-fx-fn updated-db)))}))))

(defn make-municipality-options-sub
  "Returns a subscription computation fn that filters municipalities by available
   filter numbers from the tab-specific available-filters."
  []
  (fn [[available-filters municipalities]]
    (when-some [muni-numbers (not-empty (:municipalities available-filters))]
      (not-empty
        (filter #(contains? muni-numbers (:municipality/number %)) municipalities)))))

;; ---- Shared subscriptions ---------------------------------------------------
;; These are owned by common so that feature modules (applications, complaints)
;; can reference them naturally via ::pr-common/data, ::pr-common/municipalities, etc.

(rf/reg-sub ::data :-> ::data)

(rf/reg-sub ::config
  :<- [::data]
  :-> :config)

(rf/reg-sub ::municipalities
  :<- [::config]
  :<- [:language]
  (fn [[config lang] _]
    (->> config
         :municipalities
         (sort-by #(municipality-model/municipality-name % lang)))))

(rf/reg-sub ::single-municipality-name
  :<- [::municipalities]
  :<- [:language]
  (fn [[municipalities lang] _]
    (some-> municipalities
            util/only
            (municipality-model/municipality-name lang))))

(rf/reg-sub ::municipality-names-by-number
  :<- [::municipalities]
  (fn [municipalities _]
    (into {}
          (map (juxt :municipality/number :municipality/names))
          municipalities)))

(rf/reg-sub ::municipality-name-by-number
  :<- [::municipality-names-by-number]
  :<- [:language]
  (fn [[number->name lang] [_ number]]
    (some-> (get number->name number)
            (i18n-model/get-text-from-entities-or-map lang))))

(rf/reg-sub ::permit-type-configs
  :<- [::config]
  :-> :permit-types)

(rf/reg-sub ::permit-type-names
  :<- [::permit-type-configs]
  :<- [:tr]
  :<- [:language]
  (fn [[permit-type-configs tr lang] _]
    (->> permit-type-configs
         (map (fn [[permit-type config]]
                [permit-type
                 (or (some-> config
                             :group.config/names
                             (i18n-model/get-text-from-entities-or-map lang))
                     (tr [permit-type]))]))
         (into {}))))

(rf/reg-sub ::permit-type-name
  :<- [::permit-type-names]
  :<- [:tr]
  (fn [[permit-type-names tr] [_ permit-type]]
    (or (get permit-type-names permit-type)
        (tr [permit-type]))))

(rf/reg-sub ::domain-group-id
  :<- [::config]
  (fn [config _]
    (-> config :domain-group :group/id)))

(defn contact-request-button
  [{:keys [tr municipal-case-id kind class-name]}]
  (let [domain-group-id (<sub [::domain-group-id])]
    (when (<sub [::landing-page-subs/contact-request-enabled?])
      [ui/button
       (p/assoc-if {:kind     (or kind :primary)
                    :size     :small
                    :on-click #(>evt [:app.front.ui.contact-request-panel/toggle-open
                                      (p/assoc-if {:domain-group-id domain-group-id}
                                                  :municipal-case-id municipal-case-id)])}
                   :class-name class-name)
       (tr [:public-map-view/contact-request])])))


;; ---- GIS map layers (for public records map view) ---------------------------

(rf/reg-event-fx ::load-public-map-layers
  (fn [_ [_ domain-group-id]]
    {:comm/command {:command    :public-records/map-layers
                    :method     :get
                    :data       {:domain-group-id domain-group-id}
                    :on-success [::load-public-map-layers.success]}}))

(rf/reg-event-db ::load-public-map-layers.success
  data-path
  (fn [db [_ _ {:keys [map-layers]}]]
    (assoc db :gis-map-layers map-layers)))

(rf/reg-sub ::gis-map-layers
  :<- [::data]
  :-> :gis-map-layers)

(defn find-search-result [db ^js js-feature search-results-key]
  (-> db
      (get-in [::data search-results-key])
      (p/find-first {:id (.getId js-feature)})))

(defn- results-by-id
  "Indexes a search-results vector by :id, so resolving a cluster's member
   features is O(features + results) instead of O(features × results) — a
   co-located cluster can be large, and the map view loads up to 10000 results."
  [db search-results-key]
  (into {} (map (juxt :id identity)) (get-in db [::data search-results-key])))

(defn select-single-feature-fx
  "Opens the clicked pin's record, replacing any cluster selection. The
   selected property is never touched: parcels get split, merged and
   renumbered, so a record's stored :property/id cannot be matched against
   the selected parcel — the pin is on the map because it lies inside the
   parcel polygon."
  [db ^js js-feature {:keys [search-results-key
                             selected-result-key
                             selected-cluster-results-key]}]
  (when-some [search-result (find-search-result db js-feature search-results-key)]
    {:db (update db ::data
                 #(-> %
                      (assoc selected-result-key search-result)
                      (dissoc selected-cluster-results-key)))}))

(defn select-combined-single-feature-fx
  "Single-feature selection across a tab's primary and secondary layers.
   Clears both layers' selected results before selecting so that at most one
   record detail is open per tab — the detail panel shows `(or primary
   secondary)`, so a stale sibling result would shadow the new selection.
   Returns nil without changing db when the feature is on neither layer."
  [db ^js js-feature primary-selection-keys secondary-selection-keys]
  (let [db (update db ::data dissoc
                   (:selected-result-key primary-selection-keys)
                   (:selected-result-key secondary-selection-keys))]
    (or (select-single-feature-fx db js-feature primary-selection-keys)
        (select-single-feature-fx db js-feature secondary-selection-keys))))

(defn select-cluster-results-fx
  "Non-separable cluster selection for the cluster results panel. Stores the
   resolved records instead of deriving them from live search results, because
   opening the panel resizes the map and triggers a narrower viewport search.
   Every rendered feature must still exist in the current results; otherwise
   the click falls through without storing an incomplete snapshot. The panel
   lists records without changing the selected property."
  [db features {:keys [search-results-key
                       selected-result-key
                       selected-cluster-results-key]}]
  (let [by-id          (results-by-id db search-results-key)
        search-results (mapv #(by-id (.getId ^js %)) features)]
    (when (and (seq search-results)
               (every? some? search-results))
      {:db (update db ::data
                   #(-> %
                        (assoc selected-cluster-results-key search-results)
                        (dissoc selected-result-key)))})))

(defn select-combined-cluster-results-fx
  "Cluster selection across a tab's primary and secondary layers — the panel
   lists every member record from both layers under the primary's
   selected-cluster-results-key. Clears both layers' selected results so a
   stale record panel cannot shadow the cluster panel. Returns nil unless every
   rendered feature still resolves on one of the current result layers."
  [db features primary-selection-keys secondary-selection-keys]
  (let [primary-by-id   (results-by-id db (:search-results-key primary-selection-keys))
        secondary-by-id (results-by-id db (:search-results-key secondary-selection-keys))
        search-results  (mapv (fn [^js feature]
                                (let [id (.getId feature)]
                                  (or (primary-by-id id)
                                      (secondary-by-id id))))
                              features)]
    (when (and (seq search-results)
               (every? some? search-results))
      {:db (update db ::data
                   #(-> %
                        (assoc (:selected-cluster-results-key primary-selection-keys) search-results)
                        (dissoc (:selected-result-key primary-selection-keys)
                                (:selected-result-key secondary-selection-keys))))})))

;; :max-cluster-zoom / :max-cluster-count-for-fx only tune the legacy
;; :cluster-feature-fx branch (used by the dashboard map); public records uses the
;; :cluster-results-fx opt-in, which they do not gate.
(defn make-map-click-handler
  "Click handler for the public records maps. The opt-in cluster-results-fx makes
   a non-separable cluster click open a panel listing the cluster's records
   instead of a property panel."
  [options]
  (map-common-util/click-handler-fn (assoc options
                                           :do-point-search? true
                                           :recenter-on-selection? true)))

(defn select-property-db
  "Selects a property, centring the map on it and closing whatever panel was open."
  [db
   {:keys [geometry-point] :as feature}
   {:keys [selected-property-key
           explicit-map-center-key
           selected-result-key
           selected-cluster-results-key]}]
  (-> db
      (assoc selected-property-key feature
             explicit-map-center-key geometry-point)
      (dissoc selected-result-key selected-cluster-results-key)))

(defn set-selected-result-db [db result selected-result-key]
  (assoc db selected-result-key result))

(defn close-selected-property-view-db [db {:keys [selected-property-key
                                                  selected-result-key
                                                  selected-cluster-results-key]}]
  (dissoc db selected-property-key selected-result-key selected-cluster-results-key))

(defn close-cluster-view-db
  "Closes only the cluster results panel, revealing the property panel
   underneath. Backs the ← button; the × is close-selected-property-view-db,
   which resets the whole right-hand side — the same ←/× split as the record
   panel and the complaints dashboard map."
  [db {:keys [selected-cluster-results-key]}]
  (dissoc db selected-cluster-results-key))

(defn close-selected-result-db
  "Closes only the record detail panel, revealing whatever the view's cond puts
   underneath it — the cluster list when one is open, otherwise the property
   panel. Backs the ← button; the × is close-selected-property-view-db, which
   resets the whole right-hand side. Same split as the complaints dashboard map
   (app.front.view.complaint.dashboard/complaint-view)."
  [db {:keys [selected-result-key]}]
  (dissoc db selected-result-key))

;; ---- Reusable address/PIN search + selected-property chip -------------------

(defn address-pin-search
  "Renders the gis-search/address-search box configured for the public records
   feature. `on-select-evt` is a re-frame event keyword dispatched with the
   selected feature, e.g. `::pr-applications/select-property`."
  [tr municipalities-sub on-select-evt]
  [gis-search/address-search
   {:class              "mb-4"
    :tr                 tr
    :domain-group-id    @(rf/subscribe [::domain-group-id])
    :municipalities     @(rf/subscribe municipalities-sub)
    :municipality-names @(rf/subscribe [::municipality-names-by-number])
    :disabled           false
    :on-select-address  #(>evt [on-select-evt %])
    :show-existing?     false
    :light              true}])

(defn selected-property-chip
  "Renders a removable chip showing the currently selected property (address +
   PIN). Visible only when `selected-property-sub` resolves to a non-nil value.
   Clicking the close button dispatches `clear-evt`."
  [tr selected-property-sub clear-evt]
  (when-some [selected-property @(rf/subscribe selected-property-sub)]
    (let [address     (-> selected-property :address :address/full-address)
          property-id (-> selected-property :property :property/id)]
      [:div.flex.items-center.gap-2.mb-4.p-3.bg-ui-light-gray.rounded
       [:div.flex-1
        [:span.font-bold (tr [:public-records/filtered-by-property]) ": "]
        [:span (or address (tr [:address/unknown-address]))]
        (when property-id
          [:span.text-gray-800 " (" property-id ")"])]
       [:button.border-0.bg-transparent.cursor-pointer.text-lg.font-bold.px-2
        {:on-click   #(>evt [clear-evt])
         :aria-label (tr [:close])
         :title      (tr [:close])}
        "×"]])))

(defn cluster-results-view
  "Right-side panel listing the member records of a clicked non-separable
   cluster (WIN-5735). `render-item` renders one record — pass the tab's
   property-result-item partially applied to tr and lang. `on-back-click` is
   optional: pass it only when a panel sits underneath this one, so ← peels
   this layer off and × closes the stack, as on the record panel."
  [{:keys [tr results on-back-click on-cancel-click render-item]}]
  [map-common-info-box/right-side-box
   {:on-back-click   on-back-click
    :on-cancel-click on-cancel-click
    :tr              tr}
   [:div.flex-grow.overflow-y-auto.px-2.pb-2
    [:h3.font-bold.text-lg.mb-2
     (tr [:public-records/records-at-this-location] [(count results)])]
    (futil/for-frag [result (sort-by :id results)]
      (render-item result))]])
